/**
 * OrganizationRoute - Wrapper component for routes with organization prefix
 * Handles routes like /kitchener/signup, /toronto/dashboard, etc.
 * Extracts organization slug from path and validates it exists
 * Redirects to welcome page if organization slug is invalid
 */

import React, { useState, useEffect } from 'react';
import { useParams, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getOrganizationFromPath } from '../utils/organizationContext';
import axios from 'axios';
import NotFound from './NotFound';

const OrganizationRoute = ({ children, requireAuth = false }) => {
  const { organizationSlug } = useParams();
  const location = useLocation();
  const { isAuthenticated, setOrganizationSlug } = useAuth();
  const [isValidating, setIsValidating] = useState(true);
  const [isValidOrg, setIsValidOrg] = useState(false);

  // Extract organization from path or params
  const orgSlug = organizationSlug || getOrganizationFromPath(location.pathname);

  // Validate organization slug exists and is active
  useEffect(() => {
    const validateOrganization = async () => {
      if (!orgSlug) {
        setIsValidating(false);
        setIsValidOrg(false);
        return;
      }

      try {
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
        const response = await axios.get(
          `${apiUrl}/api/organizations`,
          {
            timeout: 10000, // 10 second timeout
            headers: {
              'Content-Type': 'application/json'
            }
          }
        );
        
        if (!response.data || !response.data.organizations) {
          console.error('Invalid response format from organizations API:', response.data);
          setIsValidOrg(false);
          return;
        }
        
        const orgs = response.data.organizations || [];
        const org = orgs.find(o => o.slug === orgSlug && o.is_active);
        
        if (org) {
          setIsValidOrg(true);
          setOrganizationSlug(orgSlug);
        } else {
          console.warn(`Organization "${orgSlug}" not found or inactive. Available orgs:`, orgs.map(o => o.slug));
          setIsValidOrg(false);
        }
      } catch (error) {
        console.error('Error validating organization:', error);
        console.error('Error details:', {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status,
          apiUrl: process.env.REACT_APP_API_URL || 'http://localhost:3001',
          orgSlug: orgSlug
        });
        // On API error, allow the route to render anyway
        // The child component (Signup, etc.) will handle showing appropriate errors
        // This prevents redirect loops and provides better UX
        setIsValidOrg(true);
      } finally {
        setIsValidating(false);
      }
    };

    validateOrganization();
  }, [orgSlug, setOrganizationSlug]);

  // Show loading while validating
  if (isValidating) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        flexDirection: 'column',
        gap: '20px'
      }}>
        <div style={{ 
          width: '40px', 
          height: '40px', 
          border: '4px solid #f3f3f3',
          borderTop: '4px solid #DC2626',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <div style={{ color: '#666', fontSize: '16px' }}>Loading...</div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (!isValidOrg) {
    return (
      <NotFound
        title="Location not found"
        message="This church location does not exist or is no longer active. Check the link or open the app from the welcome page."
      />
    );
  }

  // If route requires auth and user is not authenticated, redirect to login with org context
  if (requireAuth && !isAuthenticated) {
    return <Navigate to={`/${orgSlug}/login`} replace />;
  }

  return <>{children}</>;
};

export default OrganizationRoute;

